/**
 * MapSlider
 *
 * Displays the configured map area with station markers
 * as a slideshow slide. Respects selected map bounds and language.
 *
 * summary:
 * - pigeon-maps (no Leaflet); fits view to saved bounds when present
 * - Fetches stations with react-query and updates on a timed interval
 * - Builds responsive HTML markers based on zoom level
 */
import React, { useState, useEffect, useMemo } from "react";
import { Map, ZoomControl } from "pigeon-maps";
import {
  fitLatLngBounds,
  kioskMapOverlayScale,
  kioskMapTileAttributionHtml,
  kioskMapTileProvider,
  useKioskPigeonMapSize,
} from "../../utils/kioskPigeonMapUtils";
import { PIGEON_MAP_LIMIT_BOUNDS_FULL_VIEWPORT } from "../../utils/constants";
import { getStationIcon } from "../../../Map/stationIcons";
import { CircularProgress, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../../context/TranslationContext";
import {
  createUrlFriendlyName,
  findStationByNameOrId,
} from "../../../../utils/rawFunction";
import { fetchKioskWeatherStationsMap } from "../../utils/kioskApi";
import { refetchIntervalFn } from "../../utils/kioskRefetch";
import { useQuery } from "react-query";
import { languageService } from "../../../../context/languageService";
import { KioskCenteredHtmlOverlay } from "../KioskPigeonOverlays";

const DEFAULT_CENTER = [72.49408333333334, -81.69515];
const DEFAULT_ZOOM = 5.2;

export default function MapSlider({ config, language, ...props }) {
  const mapConfig = config?.config?.map;
  const center = Array.isArray(mapConfig?.center)
    ? [parseFloat(mapConfig.center[0]), parseFloat(mapConfig.center[1])]
    : DEFAULT_CENTER;
  const zoom = mapConfig?.zoom ? parseFloat(mapConfig.zoom) : DEFAULT_ZOOM;
  const bounds = Array.isArray(mapConfig?.bounds)
    ? [
        [
          parseFloat(mapConfig.bounds[0][0]),
          parseFloat(mapConfig.bounds[0][1]),
        ],
        [
          parseFloat(mapConfig.bounds[1][0]),
          parseFloat(mapConfig.bounds[1][1]),
        ],
      ]
    : undefined;

  const [mapContainerRef, mapSize] = useKioskPigeonMapSize();
  const { translate, setCurrentLanguage } = useTranslation();
  const navigate = useNavigate();
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const [mapCenter, setMapCenter] = useState(center);
  const [mapZoom, setMapZoom] = useState(zoom);

  useEffect(() => {
    if (language) {
      const currentUserLanguage = languageService.getPreferredLanguage();
      const languageToUse =
        language === "inuktitut" && currentUserLanguage === "syllabics"
          ? "syllabics"
          : language;
      console.log(
        `MapSlider: Setting language from ${language} to ${languageToUse} (user pref: ${currentUserLanguage})`
      );
      setCurrentLanguage(languageToUse);
    }
  }, [language, setCurrentLanguage]);

  const {
    data: weatherStations,
    isLoading: loading,
  } = useQuery(["kioskWeatherStationsMap"], fetchKioskWeatherStationsMap, {
    refetchInterval: refetchIntervalFn,
    refetchIntervalInBackground: true,
    retry: 1,
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (bounds && mapSize.width > 0 && mapSize.height > 0) {
      const fitted = fitLatLngBounds(bounds, mapSize.width, mapSize.height, 3, 10);
      if (fitted) {
        setMapCenter(fitted.center);
        setMapZoom(fitted.zoom);
        setCurrentZoom(fitted.zoom);
      }
    } else {
      setMapCenter(center);
      setMapZoom(zoom);
      setCurrentZoom(zoom);
    }
  }, [bounds, mapSize.width, mapSize.height, center, zoom]);

  const filteredStations = weatherStations || [];
  const showLoadingIndicator = loading;

  const createCustomIconHtml = (
    stationName,
    info,
    hasError = false,
    status,
    stationType
  ) => {
    let iconColor;
    let statusText;
    let textColor;
    let opacity = 1;
    const stationIconConfig = getStationIcon(stationType);
    switch (status) {
      case "Non-Operational":
        statusText = "Non-Operational";
        textColor = "#ad4242";
        opacity = 0.6;
        iconColor = "#ad4242";
        break;
      case "Warning":
        iconColor = "#f2d666";
        statusText = "Warning";
        textColor = "#d4a012";
        break;
      case "Operational":
        iconColor = "#78a62b";
        statusText = info || "Operational";
        textColor = "blue";
        break;
      default:
        iconColor = "#E0E0E0";
        statusText = "Unknown Status";
        textColor = "#666666";
        opacity = 0.7;
    }
    if (hasError) {
      iconColor = "#ad4242";
      statusText = "Error";
      textColor = "#ad4242";
      opacity = 0.6;
    }
    const zoomFactor = kioskMapOverlayScale(currentZoom, DEFAULT_ZOOM);
    const baseSize = Math.max(20, Math.min(52, 32 * zoomFactor));
    const fontSize = Math.max(8, Math.min(13, 9 * Math.min(zoomFactor, 1.25)));
    return `
      <div style='text-align: center; position: relative;'>
        <div style='position: relative; display: inline-block;'>
          <img 
            src='${stationIconConfig.icon}' 
            style='
              width: ${baseSize}px; 
              height: ${baseSize}px; 
              opacity: ${opacity};
              filter: drop-shadow(0 2px 2px rgba(0,0,0,0.2));
            '
          />
          ${
            hasError || status === "Non-Operational"
              ? `<div style="position: absolute; top: -5px; right: -5px; background-color: ${iconColor}; color: white; border-radius: 50%; width: ${
                  fontSize + 4
                }px; height: ${fontSize + 4}px; font-size: ${
                  fontSize + 2
                }px; line-height: ${
                  fontSize + 4
                }px; text-align: center; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">×</div>`
              : ""
          }
        </div>
        <div style='font-size: ${fontSize}px; color: #333; font-weight: 500; margin-top: 2px; text-shadow: 0 1px 2px rgba(255,255,255,0.8);'>${translate(
          stationName
        )}</div><div style='font-size: ${
          fontSize - 2
        }px; color: ${textColor}; background-color: rgba(255,255,255,0.9); padding: 2px 4px; border-radius: 8px; margin-top: 2px; font-weight: 500; box-shadow: 0 1px 3px rgba(0,0,0,0.1); display: inline-block; width: fit-content; margin-left: auto; margin-right: auto;'>${translate(
          statusText
        )}</div>
      </div>
    `;
  };

  const handleStationClick = (stationId) => {
    const station = findStationByNameOrId(stationId);
    if (station) {
      const urlFriendlyName = createUrlFriendlyName(station.name);
      navigate(`/${urlFriendlyName}`);
    }
  };

  const attributionEl = useMemo(
    () => (
      <span dangerouslySetInnerHTML={{ __html: kioskMapTileAttributionHtml }} />
    ),
    []
  );

  return (
    <>
      <Box
        ref={mapContainerRef}
        className="kiosk-pigeon-map-root"
        sx={{ height: "100%", width: "100%", position: "relative" }}
      >
        <Map
          width={mapSize.width}
          height={mapSize.height}
          center={mapCenter}
          zoom={mapZoom}
          animate={false}
          provider={kioskMapTileProvider}
          attribution={attributionEl}
          attributionPrefix={false}
          minZoom={3}
          maxZoom={10}
          limitBounds={PIGEON_MAP_LIMIT_BOUNDS_FULL_VIEWPORT}
          boxClassname="kiosk-pigeon-map-tiles"
          onBoundsChanged={({ center: c, zoom: z }) => {
            setMapCenter(c);
            setMapZoom(z);
            setCurrentZoom(z);
          }}
        >
          {filteredStations.map((station) => {
            const html = createCustomIconHtml(
              station.name,
              station.info,
              station.hasError,
              station.status,
              station.type
            );
            return (
              <KioskCenteredHtmlOverlay
                key={station.id}
                anchor={[station.lat, station.lon]}
                html={html}
                onClick={() => handleStationClick(station.id)}
              />
            );
          })}
          <ZoomControl
            style={{ top: "auto", left: "auto", bottom: 16, right: 16 }}
          />
        </Map>
      </Box>
      {showLoadingIndicator && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            padding: "20px",
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}
        >
          <CircularProgress size={50} />
          <Box sx={{ mt: 2 }}>Loading...</Box>
        </Box>
      )}
    </>
  );
}
